import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/uxp-photoshop/uxp-photoshop/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "how-do-i"
    }}>{`How Do I...`}</h1>
    <p>{`This section provides snippets of HTML, CMS, and JavaScript code needed to accomplish common operations needed in many plugins. There are no complete plugins in this section, only code snippets. Copy and paste these snippets into sections of your plugin as needed.`}</p>
    <h2 {...{
      "id": "basic-javascript"
    }}>{`Basic JavaScript`}</h2>
    <p>{`If you're already a JavaScript expert, skip this section. But if you're coming to UXP from ExtendScript, you might want to read it.`}</p>
    <h3 {...{
      "id": "how-to-include-an-external-javascript-file"
    }}>{`How to `}<inlineCode parentName="h3">{`#include`}</inlineCode>{` An External JavaScript File`}</h3>
    <p>{`JavaScript has never had the `}<inlineCode parentName="p">{`#include`}</inlineCode>{` syntax of C and its descendants, nor the `}<inlineCode parentName="p">{`import`}</inlineCode>{` of Python. However, recent JavaScript (including that used in UXP) has "modules" and the `}<inlineCode parentName="p">{`require`}</inlineCode>{` statement. Here's how to use them:`}</p>
    <p>{`In the JavaScript file you want to be included, structure it like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// file "includeMe.js"
// this file will be included by some other file
function foo(someNumber) {
    return(someNumber * 2);
}

function bar() {
    return(42);
}
module.exports = {
    foo,
    bar
}
`}</code></pre>
    <p>{`and then in the file that wants to include the above file, add this line at the top:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const {foo, bar} = require("./includeMe.js");
. . .
    result = foo(3); // will return 6
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`require`}</inlineCode>{` in UXP isn't as robust as some other include systems (e.g., it doesn't search any global paths to find the file you want), but careful use of relative paths allows you to structure your plugin directory the way you want. For example, you can have a subdirectory named `}<inlineCode parentName="p">{`lib`}</inlineCode>{` and require a file in there this way:`}</p>
    <p><inlineCode parentName="p">{`const {foo, bar} = require("./lib/includeMe.js");`}</inlineCode></p>
    <h3 {...{
      "id": "how-to-write-debugging-statements"
    }}>{`How to write debugging statements`}</h3>
    <p>{`This one is easy. It's no longer `}<inlineCode parentName="p">{`console.writeln()`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`console.log("foo"); // writes "foo" to the UXP Developer Tool console.

console.error("foo"); // does the same thing, but the text is shown in red so errors are more easily seen.
`}</code></pre>
    <h3 {...{
      "id": "how-to-use-promises-and-asyncawait"
    }}>{`How to Use Promises and async/await`}</h3>
    <p>{`This is one of the most puzzling features of modern JavaScript for those new to the latest language versions. Basically, `}<inlineCode parentName="p">{`await`}</inlineCode>{` allows your code to wait for an operation to complete, without blocking the host application (e.g., Photoshop). But the trick is you `}<em parentName="p">{`cannot`}</em>{` use `}<inlineCode parentName="p">{`await`}</inlineCode>{` in a regular function; any function in which `}<inlineCode parentName="p">{`await`}</inlineCode>{` is used has to be declared like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`    async function foo(argument1, argument2...) {

    }
`}</code></pre>
    <p>{`This is a very cavilier treatment of `}<inlineCode parentName="p">{`async`}</inlineCode>{` and `}<inlineCode parentName="p">{`await`}</inlineCode>{`, which are actually part of a larger `}<inlineCode parentName="p">{`promises`}</inlineCode>{` feature in JavaScript. For the real story, consult your favorite search engine. You might start with the highly regarded MDN page `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/await"
      }}>{`here`}</a>{`.`}</p>
    <h2 {...{
      "id": "uxp-specific-snippets"
    }}>{`UXP-specific Snippets`}</h2>
    <h3 {...{
      "id": "how-to-determine-the-host-and-os-a-plugin-is-using"
    }}>{`How to determine the host and OS a plugin is using`}</h3>
    <p>{`Sometimes it's necesssary (for localization or other reasons) to find out what a plugin's host environment is. Here's how to do that:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const host = require('uxp').host;
const locale = host.uiLocale;
const hostName = host.name
const hostVersion = host.version;
const hostOS = require('os').platform(); // note that this is a method, not a property
console.log(\`locale: \${locale}  host \${hostName} version \${hostVersion} running on \${hostOS}\`);
`}</code></pre>
    <h3 {...{
      "id": "how-to-get-notified-that-your-panel-is-opening-or-closing"
    }}>{`How to get notified that your panel is opening or closing`}</h3>
    <p>{`In your startup code, add something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`document.addEventListener('uxpcommand', (event) => {
  const { commandId } = event
  if (commandId === 'uxpshowpanel') {
    console.log("panel is showing");
  } else if (commandId === 'uxphidepanel') {
    console.log('panel is hiding');
  }
})
`}</code></pre>
    <h2 {...{
      "id": "file-io"
    }}>{`File I/O`}</h2>
    <p>{`Files and folders in UXP for Photoshop can exist in four different places:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Your plugin's home directory. Files in this directory are `}<em parentName="p">{`read-only`}</em>{`. This is a good place to keep localizations, data files that never change, etc.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Your plugin's data directory. This is a directory managed by UXP which allows read/write access for `}<em parentName="p">{`only`}</em>{` your plugin. Files in this directory survive application restarts and OS reboots.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Your plugin's temp directory. This is a place to store session-specific data which will not necessarily persist. You shouldn't rely on anything in this directory lasting beyond an application restart.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The host OS filesystem. Access to specific files or folders in the computer's filesystem must be requested of the user by your plugin, by showing a file or folder picker. If the user selects a file and does not cancel, your plugin code receives a `}<inlineCode parentName="p">{`token`}</inlineCode>{` which you can use to access that file or folder.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`UXP also provids `}<inlineCode parentName="p">{`Secure Storage`}</inlineCode>{`. This is a UXP-managed facility that holds encrypted key/value pairs. This is good for storing login information and any other data that needs to be securely stored. As such, it's not for general file access, but if you're not writing a lot of data it might be appropriate.`}</p>
      </li>
    </ul>
    <h3 {...{
      "id": "how-to-read-a-file"
    }}>{`How to Read a File`}</h3>
    <p>{`Coming soon.`}</p>
    <h3 {...{
      "id": "how-to-write-a-file"
    }}>{`How to Write a File`}</h3>
    <p>{`Coming Soon`}</p>
    <h2 {...{
      "id": "other-useful-snippets"
    }}>{`Other Useful Snippets`}</h2>
    <h3 {...{
      "id": "how-to-display-a-simple-alert"
    }}>{`How to Display a Simple Alert`}</h3>
    <p>{`Sometimes you don't need a complicated dialog box, just a simple alert message. If you don't need any icons or special branding in your alert, this will do the trick:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const psCore = require('photoshop').core;
psCore.showAlert({ message: 'Warp Factor 9!'});
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      